/**
 * Redirects the user from the StudentPortal to the **standalone** Sandbox app,
 * handing off the shared cy-sso auth tokens via first-party cookies scoped to the
 * domain both apps share (e.g. ".codeyoung.com", ".dev.codeyoung.com", or
 * host-only "localhost").
 *
 * Portal-side mirror of sandbox/src/Core/utils/redirectToSandbox.js — the only
 * difference is the token source (portal's `localStorage.user` vs sandbox's
 * `sandbox_user`). The standalone Sandbox reads the `Authorization` +
 * `refresh-token` cookies on boot (bootstrapSessionFromCookie) and rebuilds its
 * session, so the user lands logged-in on `/home` without a second login.
 */

import {
  setCookie,
  getCookie,
  deleteCookieAllDomains,
  getSharedCookieDomain,
} from "./cookieUtils";
import { decrypt } from "./other";

const ONE_YEAR_SECONDS = 365 * 24 * 60 * 60;

// Avoids firing the full-page redirect more than once (e.g. a re-entrant guard
// evaluation before the navigation actually unloads the page).
let redirecting = false;

/**
 * Standalone Sandbox origin to redirect to. DERIVED FROM THE CURRENT HOST so it
 * always targets the same environment the user is in — never a wrong-env jump
 * (the old `REACT_APP_SANDBOX_URL || prod` fallback sent stage users to prod,
 * because that var is absent from portal's `.env.production`):
 *
 *   studentportal.dev.codeyoung.com → https://sandbox.dev.codeyoung.com/
 *   studentportal.codeyoung.com     → https://sandbox.codeyoung.com/
 *   localhost                       → REACT_APP_SANDBOX_URL or :3001 (cross-port)
 */
const getSandboxBase = () => {
  const host = window.location.hostname;
  if (host === "localhost") {
    const base = process.env.REACT_APP_SANDBOX_URL || "http://localhost:3001/";
    return base.endsWith("/") ? base : base + "/";
  }
  const parent = host.split(".").slice(1).join(".");
  return parent ? `https://sandbox.${parent}/` : "https://sandbox.codeyoung.com/";
};


/**
 * Sets the cy-sso `Authorization` + `refresh-token` cookies on the shared domain
 * and sends the user to the standalone Sandbox app — in place by default, or in
 * a new tab when asked.
 *
 * @param {string} [path="/home"] Sandbox hash route to land on after redirect.
 * @param {Object} [options]
 * @param {boolean} [options.newTab=false] Open Sandbox in a new tab instead of
 *   navigating this one. Ignored inside the native app's WebView (see below).
 *   Callers must invoke this synchronously from a user gesture (e.g. a click
 *   handler) or the popup will be blocked.
 */
export const redirectToSandbox = (path = "/home", { newTab = false } = {}) => {
  // A new tab is only possible in a real browser. Inside StudentApp's WebView
  // `window.open` does nothing — react-native-webview needs
  // `setSupportMultipleWindows` plus an `onOpenWindow` handler and the app wires
  // up neither — so there we always navigate in place, which is also what
  // carries the cookie handoff into Sandbox (an external browser would not
  // share the WebView's cookie jar at all).
  const openInNewTab = newTab && !window.ReactNativeWebView;

  // The one-shot guard protects the in-place navigation, which unloads this
  // page. A new tab leaves the portal open, so latching it there would turn
  // every later click into a no-op.
  if (!openInNewTab && redirecting) return;

  let user = {};
  try {
    user = decrypt(localStorage.getItem("user")) || {};
  } catch {
    user = {};
  }
  const access = user.ACCESS_TOKEN || user.Authorization;
  const refresh = user.REFRESH_TOKEN || user["refresh-token"];

  const base = getSandboxBase();

  // Skip re-writing cookies when they're already set to the same tokens.
  const alreadySet =
    getCookie("Authorization") === access &&
    getCookie("refresh-token") === refresh;

  if (access && refresh && !alreadySet) {
    try {
      const domain = getSharedCookieDomain();
      // Clear stale same-named cookies on other domains first so there is exactly
      // one auth cookie, on the canonical shared domain (see saveSession.js).
      deleteCookieAllDomains("Authorization");
      deleteCookieAllDomains("refresh-token");

      setCookie("Authorization", access, {
        domain,
        maxAge: ONE_YEAR_SECONDS,
        sameSite: "Lax",
      });
      setCookie("refresh-token", refresh, {
        domain,
        maxAge: ONE_YEAR_SECONDS,
        sameSite: "Lax",
      });
      // Sync flag: tells the standalone Sandbox (and any already-open tab) to
      // re-read the shared cookies and adopt these tokens (source of truth) on
      // boot/focus — covers the "switch profile in portal, then open Sandbox"
      // case where Sandbox would otherwise keep the old tokens.
      setCookie("cy_auth_sync", String(Date.now()), {
        domain,
        maxAge: ONE_YEAR_SECONDS,
        sameSite: "Lax",
      });
    } catch {
      /* still redirect even if cookie writing fails */
    }
  }

  const hash = path.startsWith("/") ? path : "/" + path;
  const url = `${base}#${hash}`;

  // The cookies above are written synchronously, so the new tab already sees
  // them on load. `window.open` returns null when a popup blocker intervenes —
  // fall through to the in-place navigation so the click always does something.
  if (openInNewTab && window.open(url, "_blank")) return;

  // Re-check the guard: the new-tab path skipped it above, so a blocked popup
  // must not fire a second in-place navigation on top of one already in flight.
  if (redirecting) return;
  redirecting = true;
  window.location.href = url;
};

export default redirectToSandbox;
